/**
 * SmartTable theme and styles — shared across Leads, Bookings, Inventory, Calls.
 * Import from here for consistent table look and for use with SmartTable.js components.
 */

// ─── Default table theme (green, matches leads/bookings) ─────────────────────
export const TABLE_THEME = {
  white:   '#ffffff',
  bg:      '#f5f8f6',
  border:  '#dce8e1',
  borderL: '#eaf1ec',
  ink:     '#0f1a13',
  ink2:    '#253b2c',
  subtle:  '#526b5c',
  muted:   '#8eaa98',
  green:   '#0a6b3e',
  headerBg: '#f8fbf9',
  bodyBg:  '#fafcfb',  // light table body background
};

/**
 * Resolve theme: use custom (e.g. lead_helper_v2.LEAD_THEME) or TABLE_THEME.
 * Theme shape: { border, borderL, subtle, ink2, headerBg, green }
 */
export const getTableTheme = (theme) => theme || TABLE_THEME;

// ─── Style getters (for SmartTable components or direct use) ─────────────────
export function getHeaderCellStyle(theme, options = {}) {
  const resolvedTheme = getTableTheme(theme);
  const { width, align = 'left' } = options;
  return {
    padding: '9px 12px',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.55px',
    color: resolvedTheme.subtle,
    whiteSpace: 'nowrap',
    background: resolvedTheme.headerBg ?? resolvedTheme.bg,
    borderBottom: `1.5px solid ${resolvedTheme.border}`,
    textAlign: align,
    userSelect: 'none',
    ...(width ? { width, minWidth: width } : {}),
  };
}

export function getBodyCellStyle(theme, options = {}) {
  const resolvedTheme = getTableTheme(theme);
  const { align = 'left', wrap = false } = options;
  return {
    padding: '10px 12px',
    verticalAlign: 'middle',
    borderBottom: `1px solid ${resolvedTheme.borderL}`,
    fontSize: 12.5,
    color: resolvedTheme.ink2,
    textAlign: align,
    background: resolvedTheme.bodyBg ?? resolvedTheme.white,
    ...(wrap ? {} : { whiteSpace: 'nowrap' }),
  };
}

export function getLoadingStateContainerStyle() {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 6,
    px: 2,
    gap: 1.5,
  };
}

export function getEmptyStateCellStyle(theme) {
  const resolvedTheme = getTableTheme(theme);
  return {
    textAlign: 'center',
    padding: '64px 20px',
    color: resolvedTheme.muted,
    fontSize: 13,
    borderBottom: `1px solid ${resolvedTheme.borderL}`,
  };
}

// ─── Row / scrollbar class names and global CSS ───────────────────────────────
export const TABLE_ROW_CLASS = 'smart-table-row';
export const TABLE_ROW_SELECTED_CLASS = 'smart-table-row-selected';

export const TABLE_SCROLLBAR_CSS = `
  .smart-table-wrap::-webkit-scrollbar { width: 5px; height: 5px; }
  .smart-table-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
  .smart-table-wrap::-webkit-scrollbar-track { background: transparent; }
`;

export const TABLE_ROW_GLOBAL_CSS = `
  .${TABLE_ROW_CLASS}:hover td { background: #f2f8f4 !important; cursor: pointer; }
  .${TABLE_ROW_CLASS}.${TABLE_ROW_SELECTED_CLASS} td { background: #ebf7f0 !important; }
  .smart-table-wrap input[type=checkbox] { accent-color: #0a6b3e; }
`;

export default {
  TABLE_THEME,
  getTableTheme,
  getHeaderCellStyle,
  getBodyCellStyle,
  getLoadingStateContainerStyle,
  getEmptyStateCellStyle,
  TABLE_ROW_CLASS,
  TABLE_ROW_SELECTED_CLASS,
  TABLE_SCROLLBAR_CSS,
  TABLE_ROW_GLOBAL_CSS,
};
